import classnames from 'classnames'
import { BadgeItem } from 'embed/components/Badges/Badge'
import { BadgeList } from 'embed/components/Badges/container'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { AprType, AprTypeOption, COMMON_CREDIT_CARD_PURPOSES, FEE_PREFIX } from 'embed/constants'

import {
  formatCurrency,
  generateAnnualFeeString,
  generateRateString,
  numberWithThousandsSeparators,
  purchaseAprString,
} from 'embed/utils'
import { CreditOfferDetails } from 'embed/constants/models'
import { APR_FIXED, APR_LABEL, APR_VARIABLE } from 'embed/utils/offer-display-utils'
import { css } from 'emotion'
import * as React from 'react'

const styles = require('./styles.scss')
const PERCENT = '%'

const STATEMENT_CREDIT = 'statement_credit'
export const SEE_TERMS = 'See Terms'
export interface CreditListItemSpecsProps {
  details: CreditOfferDetails
  rewardsEnabled: boolean
}

export const CreditListItemSpecs = (props: CreditListItemSpecsProps) => {
  const {
    details: {
      annualFee,
      annualIntroFee,
      annualIntroFeeTerm,
      aprType,
      cardName,
      cardPurposes,
      foreignExchangeFee,
      introOfferAmount,
      introOfferText,
      introOfferType,
      maxAnnualFee,
      minAnnualFee,
      maxPurchaseApr,
      minPurchaseApr,
      maxPurchaseIntroApr,
      minPurchaseIntroApr,
      purchaseAprText,
      purchaseIntroAprTerm,
      purchaseIntroAprTermUnit,
      recommendedCreditRatings,
    },
    rewardsEnabled,
  } = props

  // Recommended Credit Rating
  let creditRatings: JSX.Element | JSX.Element[]

  if (recommendedCreditRatings?.length === 5) {
    creditRatings = <span className={styles.recommendedCreditItem}>Any</span>
  } else {
    creditRatings = recommendedCreditRatings?.map((value, key) => (
      <span className={styles.recommendedCreditItem} key={`${key}`}>{`${value}`}</span>
    )) ?? <span className={styles.recommendedCreditItem}>{SEE_TERMS}</span>
  }

  const formattedIntroOfferAmount =
    introOfferType === STATEMENT_CREDIT && introOfferAmount !== undefined
      ? `${FEE_PREFIX}${formatCurrency(introOfferAmount)}`
      : `${numberWithThousandsSeparators(introOfferAmount)}`

  const formattedIntroOfferType = introOfferType?.replace('_', ' ')

  // Intro Offer Amount Header
  const introOfferAmountHeader: JSX.Element = (
    <div className={styles.introOfferAmountHeader}>
      <span>
        {formattedIntroOfferAmount} {formattedIntroOfferType}
      </span>
    </div>
  )

  // Purchase APR
  const purchaseApr = purchaseAprString(minPurchaseApr, maxPurchaseApr, purchaseAprText) ?? SEE_TERMS

  // Annual Fee
  const formattedAnnualFee =
    generateAnnualFeeString(minAnnualFee, maxAnnualFee, annualFee, annualIntroFee, annualIntroFeeTerm) ?? SEE_TERMS

  // Intro Purchase APR
  const introApr = generateRateString(
    minPurchaseIntroApr,
    maxPurchaseIntroApr,
    purchaseIntroAprTerm,
    purchaseIntroAprTermUnit,
  )

  let aprTypeCopy = APR_LABEL
  if (aprType) {
    if (aprType === AprType.Fixed) {
      aprTypeCopy = AprTypeOption[APR_FIXED]
    } else if (aprType === AprType.Variable) {
      aprTypeCopy = AprTypeOption[APR_VARIABLE]
    }
  }

  const cardBadges: BadgeItem[] = []

  for (const purpose of COMMON_CREDIT_CARD_PURPOSES) {
    const cardPurposeExists = cardPurposes?.find((existingPurpose) => existingPurpose === purpose.value)
    if (cardPurposeExists) {
      cardBadges.push({
        label: purpose.label,
      })
    }
  }

  return (
    <ThemeConsumer>
      {({ colorPrimary, colorBorder }) => (
        <div className={styles.creditOfferSpecsContainer}>
          <div className={styles.cardNameContainer}>
            <span className={classnames(styles.cardName, styles.bold)}>{cardName}</span>
            <div className={styles.badgesList}>
              <BadgeList header="Good for" items={cardBadges} />
            </div>
          </div>
          <div className={styles.creditOfferSpecs}>
            {introOfferAmount && (
              <div
                className={classnames(
                  styles.introOfferSpec,
                  styles.creditOfferSpec,
                  css`
                    border-bottom: solid 1px ${colorBorder};
                  `,
                )}
              >
                <div className={styles.specLabel}>Intro Offer</div>
                <div className={styles.specValue} data-test-id="intro_offer">
                  <div className={styles.introOffer}>
                    <div
                      className={classnames(css`
                        color: ${colorPrimary};
                      `)}
                    >
                      {introOfferAmountHeader}
                    </div>
                    {introOfferText}
                  </div>
                </div>
              </div>
            )}
            {introApr && (
              <div
                className={classnames(
                  styles.creditOfferSpec,
                  css`
                    border-bottom: solid 1px ${colorBorder};
                  `,
                )}
              >
                <div className={styles.specLabel}>Intro Purchase APR</div>
                <div className={styles.specValue} data-test-id="intro_apr">
                  {introApr}
                </div>
              </div>
            )}
            <div
              className={classnames(
                styles.creditOfferSpec,
                css`
                  border-bottom: solid 1px ${colorBorder};
                `,
              )}
            >
              <div className={styles.specLabel}>Annual Fee</div>
              <div className={styles.specValue} data-test-id="annual_fee">
                {formattedAnnualFee}
              </div>
            </div>
            <div
              className={classnames(
                styles.creditOfferSpec,
                css`
                  border-bottom: solid 1px ${colorBorder};
                `,
              )}
            >
              <div className={styles.specLabel} data-test-id="credit_offer_apr_type_label">
                {aprTypeCopy}
              </div>
              <div className={styles.specValue} data-test-id="purchase_apr">
                {purchaseApr}
              </div>
            </div>
            {foreignExchangeFee !== undefined && !isNaN(foreignExchangeFee) && (
              <div
                className={classnames(
                  styles.creditOfferSpec,
                  css`
                    border-bottom: solid 1px ${colorBorder};
                  `,
                )}
              >
                <div className={styles.specLabel}>Foreign Transaction Fee</div>
                <div className={styles.specValue} data-test-id="foreign_exchange_fee">
                  {foreignExchangeFee}
                  {PERCENT}
                </div>
              </div>
            )}
            <div
              className={classnames(
                styles.creditOfferSpec,
                css`
                  border-bottom: solid 1px ${colorBorder};
                `,
              )}
            >
              <div className={styles.specLabel}>Recommended Credit</div>
              <div>
                <span
                  className={classnames(
                    styles.specValue,
                    css`
                      color: ${colorPrimary};
                    `,
                  )}
                  data-test-id="recommended_credit"
                >
                  {creditRatings}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </ThemeConsumer>
  )
}
